import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  getSlideMarkerQcBarcode,
  getSlideMarkerQcSummary,
  SlideMarkerQcBarcodeResponse,
  SlideMarkerQcSummary,
} from 'api/markerTypes';
import PlotlyChartActions from 'components/Plotly/PlotlyChartActions';
import ThemedPlotlyFigure from 'components/Plotly/ThemedPlotlyFigure';
import { PlotlyFigure } from 'interfaces/agentSession';
import { QCLabel } from 'interfaces/qcMarkers';
import { compact, keys, sum, uniq } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useQCLabels } from 'utils/queryHooks/useQCMarkers';
import { useCasesParams } from 'utils/useCasesParams';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import ImpactIcon from './ImpactIcon';

interface QCMarkerBarcodeChartProps {
  studyId: string;
  qcMarkersSettings?: {
    [markerId: string]: string;
  };
  markerIds?: string[];
  scope?: 'dataset' | 'study';
  renderMode?: 'all' | 'summary' | 'barcode';
  showGlobalImpact?: boolean;
  onShowGlobalImpactChange?: (checked: boolean) => void;
  barcodeColorBy?: BarcodeColorMode;
  onBarcodeColorByChange?: (value: BarcodeColorMode) => void;
}

const barcodeCellWidth = 12;
const barcodeCellGap = 2;

type MarkerQcSummaryStatusId =
  | 'missing_marker'
  | 'present_without_qc'
  | 'usable'
  | 'flagged'
  | 'exclude'
  | 'other_qc_label';

interface MarkerQcSummaryRow {
  markerId: string;
  markerLabel: string;
  totalSlides: number;
  counts: Record<MarkerQcSummaryStatusId, number>;
}

const MARKER_QC_SUMMARY_SEGMENTS: Array<{
  id: MarkerQcSummaryStatusId;
  label: string;
  color: string;
  textColor: string;
}> = [
  {
    id: 'missing_marker',
    label: 'Missing marker',
    color: 'rgba(148, 163, 184, 0.36)',
    textColor: 'rgba(15, 23, 42, 0.92)',
  },
  {
    id: 'present_without_qc',
    label: 'No QC label',
    color: 'rgba(33, 33, 33, 0.2)',
    textColor: 'rgba(15, 23, 42, 0.92)',
  },
  {
    id: 'usable',
    label: 'Usable',
    color: 'rgba(46, 125, 50, 0.68)',
    textColor: '#fff',
  },
  {
    id: 'flagged',
    label: 'Flagged',
    color: 'rgba(239, 108, 0, 0.74)',
    textColor: '#fff',
  },
  {
    id: 'exclude',
    label: 'Exclude',
    color: 'rgba(211, 47, 47, 0.82)',
    textColor: '#fff',
  },
  {
    id: 'other_qc_label',
    label: 'Other QC label',
    color: 'rgba(25, 118, 210, 0.68)',
    textColor: '#fff',
  },
];

const DISPLAYED_MARKER_QC_SUMMARY_SEGMENTS = MARKER_QC_SUMMARY_SEGMENTS.filter(
  (segment) => segment.id !== 'present_without_qc'
);

const EMPTY_MARKER_QC_SUMMARY_COUNTS: Record<MarkerQcSummaryStatusId, number> = {
  missing_marker: 0,
  present_without_qc: 0,
  usable: 0,
  flagged: 0,
  exclude: 0,
  other_qc_label: 0,
};

const normalizeQcLabelId = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const normalizedValue = value.trim();
    return normalizedValue || null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const normalizedItem = normalizeQcLabelId(item);
      if (normalizedItem) {
        return normalizedItem;
      }
    }

    return null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const qcLabelShape = value as Record<string, unknown>;
  return (
    normalizeQcLabelId(qcLabelShape.qcLabelId) ||
    normalizeQcLabelId(qcLabelShape.qc_label_id) ||
    normalizeQcLabelId(qcLabelShape.labelId) ||
    normalizeQcLabelId(qcLabelShape.label_id) ||
    normalizeQcLabelId(qcLabelShape.id) ||
    normalizeQcLabelId(qcLabelShape.value)
  );
};

const getSlideLevelQcMarkerIds = (slideMarkerQcSummary: SlideMarkerQcSummary[] | undefined) => {
  if (!slideMarkerQcSummary?.length) {
    return [] as string[];
  }

  return uniq(
    compact(
      slideMarkerQcSummary.flatMap((slideSummary) =>
        Object.entries(slideSummary.channelMarkersQc || {}).map(([channelId, qcValue]) => {
          if (!normalizeQcLabelId(qcValue)) {
            return null;
          }

          return slideSummary.channelMarkerTypes?.[channelId] || null;
        })
      )
    )
  );
};

const buildSlideMarkerImpactByMarker = (
  slideSummary: SlideMarkerQcSummary,
  qcLabelsById: Record<string, QCLabel>,
  cohortMarkerQcSettings: Record<string, string>
) => {
  const cellsByMarker: Record<
    string,
    {
      hasQcLabel: boolean;
      impactId?: string;
    }
  > = {};

  Object.entries(slideSummary.channelMarkerTypes || {}).forEach(([channelId, markerId]) => {
    if (!markerId) {
      return;
    }

    const qcLabelId = normalizeQcLabelId(
      (slideSummary.channelMarkersQc as Record<string, unknown> | null | undefined)?.[channelId]
    );
    const cohortImpactId = !qcLabelId ? cohortMarkerQcSettings[markerId] : undefined;
    cellsByMarker[markerId] = {
      hasQcLabel: Boolean(qcLabelId || cohortImpactId),
      impactId: qcLabelId ? qcLabelsById[qcLabelId]?.impactId : cohortImpactId,
    };
  });

  return cellsByMarker;
};

const buildMarkerQcSummaryRows = ({
  markerIds,
  qcLabels,
  slideMarkerQcSummary,
  cohortMarkerQcSettings,
  stainTypeIdToDisplayName,
}: {
  markerIds: string[];
  qcLabels: QCLabel[];
  slideMarkerQcSummary: SlideMarkerQcSummary[];
  cohortMarkerQcSettings: Record<string, string>;
  stainTypeIdToDisplayName: (value: string) => string;
}): MarkerQcSummaryRow[] => {
  if (!markerIds.length || !slideMarkerQcSummary.length) {
    return [];
  }

  const qcLabelsById = Object.fromEntries(qcLabels.map((qcLabel) => [qcLabel.id, qcLabel]));
  const perSlideMarkerImpacts = slideMarkerQcSummary.map((slideSummary) =>
    buildSlideMarkerImpactByMarker(slideSummary, qcLabelsById, cohortMarkerQcSettings)
  );

  return markerIds.map((markerId) => {
    const counts = { ...EMPTY_MARKER_QC_SUMMARY_COUNTS };

    perSlideMarkerImpacts.forEach((slideMarkerImpacts) => {
      const markerImpact = slideMarkerImpacts[markerId];
      if (!markerImpact) {
        counts.missing_marker += 1;
        return;
      }

      if (!markerImpact.hasQcLabel) {
        counts.present_without_qc += 1;
        return;
      }

      if (markerImpact.impactId === 'usable') {
        counts.usable += 1;
        return;
      }

      if (markerImpact.impactId === 'flagged') {
        counts.flagged += 1;
        return;
      }

      if (markerImpact.impactId === 'exclude') {
        counts.exclude += 1;
        return;
      }

      counts.other_qc_label += 1;
    });

    return {
      markerId,
      markerLabel: stainTypeIdToDisplayName(markerId) || markerId,
      totalSlides: slideMarkerQcSummary.length,
      counts,
    };
  });
};

const getDisplayedMarkerQcSummaryTotal = (markerSummaryRow: MarkerQcSummaryRow) =>
  sum(DISPLAYED_MARKER_QC_SUMMARY_SEGMENTS.map((segment) => markerSummaryRow.counts[segment.id]));

const withAlpha = (rgbaColor: string, alpha: number) => {
  const rgbaMatch = rgbaColor.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/i);

  if (!rgbaMatch) {
    return rgbaColor;
  }

  return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
};

const getContrastingTextColor = (rgbaColor: string) => {
  const rgbaMatch = rgbaColor.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/i);

  if (!rgbaMatch) {
    return '#fff';
  }

  const red = Number(rgbaMatch[1]);
  const green = Number(rgbaMatch[2]);
  const blue = Number(rgbaMatch[3]);
  const relativeLuminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

  return relativeLuminance > 0.62 ? 'rgba(15, 23, 42, 0.92)' : '#fff';
};

const getMarkerQcSummaryFigureHeight = ({
  markerCount,
  maxDisplayedStackValue,
}: {
  markerCount: number;
  maxDisplayedStackValue: number;
}) => Math.max(280, markerCount * 40 + 120, Math.min(520, maxDisplayedStackValue * 18 + 160));

const getMarkerQcSummaryYAxisMax = (maxDisplayedStackValue: number) =>
  Math.max(1, maxDisplayedStackValue + Math.max(1, maxDisplayedStackValue * 0.2));

const buildMarkerQcSummaryValueChips = ({
  displayedMarkerSummaryRows,
  figureHeight,
  yAxisMax,
}: {
  displayedMarkerSummaryRows: MarkerQcSummaryRow[];
  figureHeight: number;
  yAxisMax: number;
}) => {
  const chartBodyHeightPx = Math.max(140, figureHeight - 176);
  const pixelsPerSlide = chartBodyHeightPx / yAxisMax;
  const chipHeightPx = 14;
  const chipRadiusPx = chipHeightPx / 2;
  const annotations: Record<string, unknown>[] = [];
  const shapes: Record<string, unknown>[] = [];

  displayedMarkerSummaryRows.forEach((markerSummaryRow, markerIndex) => {
    let segmentBase = 0;

    DISPLAYED_MARKER_QC_SUMMARY_SEGMENTS.forEach((segment) => {
      const segmentCount = markerSummaryRow.counts[segment.id];

      if (!segmentCount) {
        return;
      }

      const segmentMidpoint = segmentBase + segmentCount / 2;
      segmentBase += segmentCount;

      if (segmentCount * pixelsPerSlide < chipHeightPx + 2) {
        return;
      }

      const chipText = String(segmentCount);
      const chipWidthPx = Math.max(18, 10 + chipText.length * 7);
      const chipHalfWidthPx = chipWidthPx / 2;
      const chipFillColor = withAlpha(segment.color, 0.94);
      const chipBorderColor = withAlpha(segment.color, 1);

      shapes.push(
        {
          type: 'rect',
          xref: 'x',
          yref: 'y',
          xsizemode: 'pixel',
          ysizemode: 'pixel',
          xanchor: markerIndex,
          yanchor: segmentMidpoint,
          x0: -chipHalfWidthPx + chipRadiusPx,
          x1: chipHalfWidthPx - chipRadiusPx,
          y0: chipHeightPx / 2,
          y1: -chipHeightPx / 2,
          line: {
            color: chipBorderColor,
            width: 1,
          },
          fillcolor: chipFillColor,
          layer: 'above',
        },
        {
          type: 'circle',
          xref: 'x',
          yref: 'y',
          xsizemode: 'pixel',
          ysizemode: 'pixel',
          xanchor: markerIndex,
          yanchor: segmentMidpoint,
          x0: -chipHalfWidthPx,
          x1: -chipHalfWidthPx + chipHeightPx,
          y0: chipHeightPx / 2,
          y1: -chipHeightPx / 2,
          line: {
            color: chipBorderColor,
            width: 1,
          },
          fillcolor: chipFillColor,
          layer: 'above',
        },
        {
          type: 'circle',
          xref: 'x',
          yref: 'y',
          xsizemode: 'pixel',
          ysizemode: 'pixel',
          xanchor: markerIndex,
          yanchor: segmentMidpoint,
          x0: chipHalfWidthPx - chipHeightPx,
          x1: chipHalfWidthPx,
          y0: chipHeightPx / 2,
          y1: -chipHeightPx / 2,
          line: {
            color: chipBorderColor,
            width: 1,
          },
          fillcolor: chipFillColor,
          layer: 'above',
        }
      );

      annotations.push({
        xref: 'x',
        yref: 'y',
        x: markerIndex,
        y: segmentMidpoint,
        text: `<b>${chipText}</b>`,
        showarrow: false,
        font: {
          color: segment.textColor,
          size: 9,
        },
        align: 'center',
        xanchor: 'center',
        yanchor: 'middle',
        bgcolor: 'rgba(0,0,0,0)',
        bordercolor: 'rgba(0,0,0,0)',
      });
    });
  });

  return {
    annotations,
    shapes,
  };
};

const buildMarkerQcSummaryFigure = (markerSummaryRows: MarkerQcSummaryRow[]): PlotlyFigure | null => {
  const displayedMarkerSummaryRows = markerSummaryRows.filter(
    (markerSummaryRow) => getDisplayedMarkerQcSummaryTotal(markerSummaryRow) > 0
  );

  if (!displayedMarkerSummaryRows.length) {
    return null;
  }

  const markerLabels = displayedMarkerSummaryRows.map((row) => row.markerLabel);
  const maxDisplayedStackValue = Math.max(
    ...displayedMarkerSummaryRows.map((markerSummaryRow) => getDisplayedMarkerQcSummaryTotal(markerSummaryRow)),
    0
  );
  const figureHeight = getMarkerQcSummaryFigureHeight({
    markerCount: displayedMarkerSummaryRows.length,
    maxDisplayedStackValue,
  });
  const yAxisMax = getMarkerQcSummaryYAxisMax(maxDisplayedStackValue);
  const valueChips = buildMarkerQcSummaryValueChips({
    displayedMarkerSummaryRows,
    figureHeight,
    yAxisMax,
  });
  const traces = DISPLAYED_MARKER_QC_SUMMARY_SEGMENTS.map((segment) => {
    const segmentCounts = displayedMarkerSummaryRows.map((row) => row.counts[segment.id]);
    if (!segmentCounts.some(Boolean)) {
      return null;
    }

    return {
      type: 'bar',
      name: segment.label,
      x: markerLabels,
      y: segmentCounts,
      customdata: displayedMarkerSummaryRows.map((row) => [row.markerLabel, segment.label, row.totalSlides]),
      marker: {
        color: segment.color,
        line: {
          color: 'rgba(255, 255, 255, 0.9)',
          width: 1,
        },
      },
      hovertemplate:
        '<b>%{customdata[0]}</b><br>Status: %{customdata[1]}<br>Slides: %{y} / %{customdata[2]}<extra></extra>',
    };
  }).filter(Boolean) as Record<string, unknown>[];

  if (!traces.length) {
    return null;
  }

  return {
    data: traces,
    layout: {
      barmode: 'stack',
      bargap: displayedMarkerSummaryRows.length <= 2 ? 0.7 : displayedMarkerSummaryRows.length <= 4 ? 0.5 : 0.34,
      margin: { l: 56, r: 24, t: 24, b: 96 },
      meta: {
        themeMode: 'client',
      },
      legend: {
        orientation: 'h',
        y: 1.16,
      },
      hovermode: 'closest',
      showlegend: true,
      annotations: valueChips.annotations,
      shapes: valueChips.shapes,
      xaxis: {
        type: 'category',
        title: { text: 'Marker' },
        automargin: true,
        tickangle: -30,
        categoryorder: 'array',
        categoryarray: markerLabels,
      },
      yaxis: {
        title: { text: 'Slides' },
        automargin: true,
        range: [0, yAxisMax],
        rangemode: 'tozero',
      },
    },
  };
};

interface SlideMarkerQcDetailRow {
  slideId: string;
  originalFileName?: string;
  markerId: string;
  markerLabel: string;
  qcLabelName: string | null;
  qcImpactId: string | null;
  globalQcImpactId: string | null;
}

const IMPACT_CHIP_MAP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  usable: { label: 'Usable', color: 'success' },
  flagged: { label: 'Flagged', color: 'warning' },
  exclude: { label: 'Exclude', color: 'error' },
};

const buildSlideMarkerQcDetailRows = ({
  slideMarkerQcSummary,
  qcLabels,
  cohortMarkerQcSettings,
  stainTypeIdToDisplayName,
}: {
  slideMarkerQcSummary: SlideMarkerQcSummary[];
  qcLabels: QCLabel[];
  cohortMarkerQcSettings: Record<string, string>;
  stainTypeIdToDisplayName: (value: string) => string;
}): SlideMarkerQcDetailRow[] => {
  if (!slideMarkerQcSummary.length) {
    return [];
  }

  const qcLabelsById = Object.fromEntries(qcLabels.map((qcLabel) => [qcLabel.id, qcLabel]));
  const rows: SlideMarkerQcDetailRow[] = [];

  slideMarkerQcSummary.forEach((slideSummary) => {
    Object.entries(slideSummary.channelMarkerTypes || {}).forEach(([channelId, markerId]) => {
      if (!markerId) {
        return;
      }

      const qcLabelId = normalizeQcLabelId(
        (slideSummary.channelMarkersQc as Record<string, unknown> | null | undefined)?.[channelId]
      );
      const qcLabel = qcLabelId ? qcLabelsById[qcLabelId] : undefined;

      rows.push({
        slideId: slideSummary.slideId,
        originalFileName: slideSummary.originalFileName,
        markerId,
        markerLabel: stainTypeIdToDisplayName(markerId) || markerId,
        qcLabelName: qcLabel?.name || (qcLabelId ? qcLabelId : null),
        qcImpactId: qcLabel?.impactId || null,
        globalQcImpactId: cohortMarkerQcSettings[markerId] || null,
      });
    });
  });

  return rows;
};

const ImpactChip: React.FC<{ impactId: string | null }> = ({ impactId }) => {
  if (!impactId) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const chipDef = IMPACT_CHIP_MAP[impactId];
  if (!chipDef) {
    return <Chip size="small" label={impactId} variant="outlined" />;
  }

  return <Chip size="small" label={chipDef.label} color={chipDef.color} variant="outlined" />;
};

interface MarkerQcGroupedSummary {
  markerId: string;
  markerLabel: string;
  totalSlides: number;
  labelCounts: Record<string, number>;
  impactCounts: Record<string, number>;
  globalImpactSlideCount: number;
  globalImpactId: string | null;
  missingCount: number;
}

const QC_LABEL_PALETTE = [
  'rgba(84, 112, 198, 0.74)',
  'rgba(145, 204, 117, 0.74)',
  'rgba(250, 200, 88, 0.74)',
  'rgba(115, 192, 222, 0.74)',
  'rgba(59, 162, 114, 0.74)',
  'rgba(252, 132, 82, 0.74)',
  'rgba(154, 96, 180, 0.74)',
  'rgba(234, 124, 204, 0.74)',
];

const IMPACT_BAR_COLORS: Record<string, string> = {
  usable: 'rgba(46, 125, 50, 0.68)',
  flagged: 'rgba(239, 108, 0, 0.74)',
  exclude: 'rgba(211, 47, 47, 0.82)',
};

const GLOBAL_IMPACT_BAR_COLORS: Record<string, string> = {
  usable: 'rgba(46, 125, 50, 0.35)',
  flagged: 'rgba(239, 108, 0, 0.35)',
  exclude: 'rgba(211, 47, 47, 0.35)',
};

const IMPACT_LABELS: Record<string, string> = {
  usable: 'Usable',
  flagged: 'Flagged',
  exclude: 'Exclude',
};

const GROUPED_SUMMARY_IMPACT_ORDER = ['usable', 'flagged', 'exclude'];

type BarcodeColorMode = 'impact' | 'label';

interface BarcodeTraceStyle {
  key: string;
  name: string;
  color: string;
  symbol: string;
  stateLabel: string;
  sourceLabel: string;
  lineWidth?: number;
}

const BARCODE_MISSING_TRACE_STYLE: BarcodeTraceStyle = {
  key: 'missing_marker',
  name: 'Marker missing on slide',
  color: 'rgba(148, 163, 184, 0.28)',
  symbol: 'square-open',
  stateLabel: 'Marker missing on slide',
  sourceLabel: 'Missing marker',
  lineWidth: 1.6,
};

const BARCODE_PRESENT_WITHOUT_QC_TRACE_STYLE: BarcodeTraceStyle = {
  key: 'present_without_qc',
  name: 'Marker present, no QC label',
  color: 'rgba(33, 33, 33, 0.22)',
  symbol: 'square',
  stateLabel: 'Marker present, no QC label',
  sourceLabel: 'No QC label',
};

const BARCODE_MANUAL_QC_FALLBACK_COLOR = 'rgba(46, 125, 50, 0.68)';
const BARCODE_GLOBAL_QC_FALLBACK_COLOR = 'rgba(46, 125, 50, 0.35)';
const BARCODE_IMPACT_ORDER = ['usable', 'flagged', 'exclude', 'qc_labeled'];

const buildMarkerQcGroupedSummary = ({
  markerIds,
  qcLabels,
  slideMarkerQcSummary,
  cohortMarkerQcSettings,
  stainTypeIdToDisplayName,
}: {
  markerIds: string[];
  qcLabels: QCLabel[];
  slideMarkerQcSummary: SlideMarkerQcSummary[];
  cohortMarkerQcSettings: Record<string, string>;
  stainTypeIdToDisplayName: (value: string) => string;
}): { summaries: MarkerQcGroupedSummary[]; uniqueLabels: string[] } => {
  if (!markerIds.length || !slideMarkerQcSummary.length) {
    return { summaries: [], uniqueLabels: [] };
  }

  const qcLabelsById = Object.fromEntries(qcLabels.map((qcLabel) => [qcLabel.id, qcLabel]));
  const allLabelNames = new Set<string>();

  const summaries = markerIds.map((markerId) => {
    const labelCounts: Record<string, number> = {};
    const impactCounts: Record<string, number> = {};
    let globalImpactSlideCount = 0;
    let missingCount = 0;

    slideMarkerQcSummary.forEach((slideSummary) => {
      const channelEntry = Object.entries(slideSummary.channelMarkerTypes || {}).find(([, mId]) => mId === markerId);

      if (!channelEntry) {
        missingCount += 1;
        return;
      }

      const [channelId] = channelEntry;
      const qcLabelId = normalizeQcLabelId((slideSummary.channelMarkersQc as Record<string, unknown>)?.[channelId]);

      if (qcLabelId) {
        const qcLabel = qcLabelsById[qcLabelId];
        const labelName = qcLabel?.name || qcLabelId;
        allLabelNames.add(labelName);
        labelCounts[labelName] = (labelCounts[labelName] || 0) + 1;

        const impactId = qcLabel?.impactId;
        if (impactId) {
          impactCounts[impactId] = (impactCounts[impactId] || 0) + 1;
        }
      } else if (cohortMarkerQcSettings[markerId]) {
        globalImpactSlideCount += 1;
      }
    });

    return {
      markerId,
      markerLabel: stainTypeIdToDisplayName(markerId) || markerId,
      totalSlides: slideMarkerQcSummary.length,
      labelCounts,
      impactCounts,
      globalImpactSlideCount,
      globalImpactId: cohortMarkerQcSettings[markerId] || null,
      missingCount,
    };
  });

  return { summaries, uniqueLabels: Array.from(allLabelNames).sort() };
};

const buildGroupedMarkerQcCountAnnotations = ({
  activeSummaries,
  uniqueLabels,
  showGlobalImpact,
}: {
  activeSummaries: MarkerQcGroupedSummary[];
  uniqueLabels: string[];
  showGlobalImpact: boolean;
}) => {
  const annotations: Record<string, unknown>[] = [];

  activeSummaries.forEach((summary) => {
    let labelBase = 0;

    uniqueLabels.forEach((labelName, labelIndex) => {
      const count = summary.labelCounts[labelName] || 0;

      if (!count) {
        return;
      }

      const chipColor = QC_LABEL_PALETTE[labelIndex % QC_LABEL_PALETTE.length];

      annotations.push({
        xref: 'x',
        yref: 'y',
        x: [summary.markerLabel, 'QC Label'],
        y: labelBase + count / 2,
        text: `<b>${count}</b>`,
        showarrow: false,
        font: {
          color: getContrastingTextColor(chipColor),
          size: 9,
        },
        xanchor: 'center',
        yanchor: 'middle',
        bgcolor: withAlpha(chipColor, 0.96),
        bordercolor: withAlpha(chipColor, 1),
        borderwidth: 1,
        borderpad: 3,
      });

      labelBase += count;
    });

    let impactBase = 0;

    GROUPED_SUMMARY_IMPACT_ORDER.forEach((impactId) => {
      const count = summary.impactCounts[impactId] || 0;

      if (!count) {
        return;
      }

      const chipColor = IMPACT_BAR_COLORS[impactId] || 'rgba(100,100,100,0.5)';

      annotations.push({
        xref: 'x',
        yref: 'y',
        x: [summary.markerLabel, 'Impact'],
        y: impactBase + count / 2,
        text: `<b>${count}</b>`,
        showarrow: false,
        font: {
          color: getContrastingTextColor(chipColor),
          size: 9,
        },
        xanchor: 'center',
        yanchor: 'middle',
        bgcolor: withAlpha(chipColor, 0.96),
        bordercolor: withAlpha(chipColor, 1),
        borderwidth: 1,
        borderpad: 3,
      });

      impactBase += count;
    });

    if (showGlobalImpact && summary.globalImpactId && summary.globalImpactSlideCount > 0) {
      const chipColor = GLOBAL_IMPACT_BAR_COLORS[summary.globalImpactId] || 'rgba(100,100,100,0.25)';

      annotations.push({
        xref: 'x',
        yref: 'y',
        x: [summary.markerLabel, 'Global QC'],
        y: summary.globalImpactSlideCount / 2,
        text: `<b>${summary.globalImpactSlideCount}</b>`,
        showarrow: false,
        font: {
          color: getContrastingTextColor(chipColor),
          size: 9,
        },
        xanchor: 'center',
        yanchor: 'middle',
        bgcolor: withAlpha(chipColor, 0.96),
        bordercolor: withAlpha(chipColor, 1),
        borderwidth: 1,
        borderpad: 3,
      });
    }
  });

  return annotations;
};

const getIntentfulWholeNumberTickStep = (maxValue: number) => {
  const roughStep = Math.max(1, maxValue / 6);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;

  if (normalized <= 1) {
    return magnitude;
  }

  if (normalized <= 2) {
    return 2 * magnitude;
  }

  if (normalized <= 3) {
    return 3 * magnitude;
  }

  if (normalized <= 5) {
    return 5 * magnitude;
  }

  return 10 * magnitude;
};

const buildGroupedMarkerQcSummaryFigure = ({
  summaries,
  uniqueLabels,
  showGlobalImpact,
}: {
  summaries: MarkerQcGroupedSummary[];
  uniqueLabels: string[];
  showGlobalImpact: boolean;
}): PlotlyFigure | null => {
  const activeSummaries = summaries.filter(
    (s) =>
      Object.values(s.labelCounts).some(Boolean) ||
      Object.values(s.impactCounts).some(Boolean) ||
      (showGlobalImpact && s.globalImpactSlideCount > 0)
  );

  if (!activeSummaries.length) {
    return null;
  }

  const markerLabels = activeSummaries.map((s) => s.markerLabel);
  const labelBarX = [markerLabels, activeSummaries.map(() => 'QC Label')];
  const impactBarX = [markerLabels, activeSummaries.map(() => 'Impact')];
  const globalBarX = [markerLabels, activeSummaries.map(() => 'Global QC')];
  const traces: Record<string, unknown>[] = [];

  uniqueLabels.forEach((labelName, labelIndex) => {
    const yCounts = activeSummaries.map((s) => s.labelCounts[labelName] || 0);
    if (!yCounts.some(Boolean)) return;

    traces.push({
      type: 'bar',
      name: labelName,
      x: labelBarX,
      y: yCounts,
      offsetgroup: 'label',
      legendgroup: 'QC Label',
      legendgrouptitle: { text: 'QC Label' },
      marker: {
        color: QC_LABEL_PALETTE[labelIndex % QC_LABEL_PALETTE.length],
        line: { color: 'rgba(255,255,255,0.9)', width: 1 },
      },
      customdata: activeSummaries.map((s) => [s.markerLabel, labelName, s.totalSlides]),
      hovertemplate:
        '<b>%{customdata[0]}</b><br>Label: %{customdata[1]}<br>Slides: %{y} / %{customdata[2]}<extra></extra>',
    });
  });

  GROUPED_SUMMARY_IMPACT_ORDER.forEach((impactId) => {
    const yCounts = activeSummaries.map((s) => s.impactCounts[impactId] || 0);
    if (!yCounts.some(Boolean)) return;

    traces.push({
      type: 'bar',
      name: IMPACT_LABELS[impactId] || impactId,
      x: impactBarX,
      y: yCounts,
      offsetgroup: 'impact',
      legendgroup: 'Impact',
      legendgrouptitle: { text: 'Impact' },
      marker: {
        color: IMPACT_BAR_COLORS[impactId] || 'rgba(100,100,100,0.5)',
        line: { color: 'rgba(255,255,255,0.9)', width: 1 },
      },
      customdata: activeSummaries.map((s) => [s.markerLabel, IMPACT_LABELS[impactId] || impactId, s.totalSlides]),
      hovertemplate:
        '<b>%{customdata[0]}</b><br>Impact: %{customdata[1]}<br>Slides: %{y} / %{customdata[2]}<extra></extra>',
    });
  });

  if (showGlobalImpact) {
    GROUPED_SUMMARY_IMPACT_ORDER.forEach((impactId) => {
      const yCounts = activeSummaries.map((s) => (s.globalImpactId === impactId ? s.globalImpactSlideCount : 0));
      if (!yCounts.some(Boolean)) return;

      traces.push({
        type: 'bar',
        name: 'Global: ' + (IMPACT_LABELS[impactId] || impactId),
        x: globalBarX,
        y: yCounts,
        offsetgroup: 'global',
        legendgroup: 'Global QC',
        legendgrouptitle: { text: 'Global QC' },
        marker: {
          color: GLOBAL_IMPACT_BAR_COLORS[impactId] || 'rgba(100,100,100,0.25)',
          line: { color: 'rgba(255,255,255,0.9)', width: 1 },
        },
        customdata: activeSummaries.map((s) => [
          s.markerLabel,
          'Global: ' + (IMPACT_LABELS[impactId] || impactId),
          s.totalSlides,
        ]),
        hovertemplate: '<b>%{customdata[0]}</b><br>%{customdata[1]}<br>Slides: %{y} / %{customdata[2]}<extra></extra>',
      });
    });
  }

  if (!traces.length) return null;

  const maxY = Math.max(
    ...activeSummaries.map((s) =>
      Math.max(
        sum(Object.values(s.labelCounts)),
        sum(Object.values(s.impactCounts)),
        showGlobalImpact ? s.globalImpactSlideCount : 0
      )
    ),
    1
  );
  const countAnnotations = buildGroupedMarkerQcCountAnnotations({
    activeSummaries,
    uniqueLabels,
    showGlobalImpact,
  });
  const yAxisTickStep = getIntentfulWholeNumberTickStep(maxY);
  const yAxisMax = Math.max(yAxisTickStep, Math.ceil((maxY * 1.15) / yAxisTickStep) * yAxisTickStep);

  return {
    data: traces,
    layout: {
      barmode: 'stack',
      bargap: activeSummaries.length <= 2 ? 0.5 : activeSummaries.length <= 4 ? 0.35 : 0.25,
      bargroupgap: 0.18,
      margin: { l: 56, r: 24, t: 24, b: 172 },
      meta: { themeMode: 'client' },
      legend: {
        orientation: 'h',
        x: 0,
        xanchor: 'left',
        y: -0.24,
        yanchor: 'top',
        tracegroupgap: 12,
      },
      hovermode: 'closest',
      showlegend: true,
      annotations: countAnnotations,
      xaxis: {
        type: 'multicategory',
        title: { text: 'Marker' },
        automargin: true,
      },
      yaxis: {
        title: { text: 'Slides' },
        automargin: true,
        range: [0, yAxisMax],
        rangemode: 'tozero',
        tickmode: 'linear',
        tick0: 0,
        dtick: yAxisTickStep,
        tickformat: ',d',
      },
    },
  };
};

const LoadingMarkerQcBarcodeChart: React.FC<{
  markerIds: string[];
  placeholderSlidesCount: number;
  slideCount?: number;
  labelledCellsCount?: number;
  qcMarkersSettings?: {
    [markerId: string]: string;
  };
  stainTypeIdToDisplayName: (value: string) => string;
}> = ({
  markerIds,
  placeholderSlidesCount,
  slideCount,
  labelledCellsCount,
  qcMarkersSettings,
  stainTypeIdToDisplayName,
}) => {
  return (
    <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
      <Typography variant="h4" gutterBottom>
        Slide-Level Marker QC
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Preparing the slide-level marker QC layout from the study marker set while the filtered slide data loads.
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
        <Typography variant="body2">Markers: {markerIds.length}</Typography>
        <Typography variant="body2">
          Slides:{' '}
          {slideCount ?? (
            <Skeleton component="span" variant="text" animation="wave" width={36} sx={{ display: 'inline-block' }} />
          )}
        </Typography>
        <Typography variant="body2">
          Labelled cells:{' '}
          {labelledCellsCount ?? (
            <Skeleton component="span" variant="text" animation="wave" width={36} sx={{ display: 'inline-block' }} />
          )}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 1 }}>
        <Box sx={{ minWidth: 260 + placeholderSlidesCount * (barcodeCellWidth + barcodeCellGap) }}>
          <Box display="flex" alignItems="center" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 2,
                width: 260,
                minWidth: 260,
                backgroundColor: 'background.paper',
                py: 0.5,
              }}
            >
              <Typography variant="subtitle2">Markers</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Loading filtered slides...
            </Typography>
          </Box>

          {markerIds.map((markerId) => (
            <Box key={markerId} display="flex" alignItems="center" sx={{ mb: 0.75 }}>
              <Box
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  width: 260,
                  minWidth: 260,
                  backgroundColor: 'background.paper',
                  pr: 2,
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <Box display="flex" alignItems="center" minWidth={0}>
                    <ImpactIcon qcImpactId={qcMarkersSettings?.[markerId] || ''} emptyIconPlaceholder={true} />
                    <Typography variant="body2" noWrap title={stainTypeIdToDisplayName(markerId) || markerId}>
                      {stainTypeIdToDisplayName(markerId) || markerId}
                    </Typography>
                  </Box>
                  <Skeleton variant="text" animation="wave" width={18} />
                </Box>
              </Box>

              <Box display="flex" gap={`${barcodeCellGap}px`}>
                {Array.from({ length: placeholderSlidesCount }).map((_, index) => (
                  <Skeleton
                    key={`${markerId}-${index}`}
                    variant="rounded"
                    animation="wave"
                    width={barcodeCellWidth}
                    height={24}
                    sx={{ borderRadius: 0.5, transform: 'none' }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const stripPlotTitle = (figure?: PlotlyFigure | null): PlotlyFigure | null => {
  if (!figure) {
    return null;
  }

  const layout = { ...(figure.layout || {}) };
  delete layout.title;

  return {
    ...figure,
    layout,
  };
};

const buildSlideMarkerQcBarcodeFigure = ({
  baseFigure,
  barcodeResponse,
  slideMarkerQcSummary,
  qcLabels,
  cohortMarkerQcSettings,
  colorBy,
  showGlobalImpact,
  stainTypeIdToDisplayName,
}: {
  baseFigure?: PlotlyFigure | null;
  barcodeResponse?: SlideMarkerQcBarcodeResponse;
  slideMarkerQcSummary: SlideMarkerQcSummary[];
  qcLabels: QCLabel[];
  cohortMarkerQcSettings: Record<string, string>;
  colorBy: BarcodeColorMode;
  showGlobalImpact: boolean;
  stainTypeIdToDisplayName: (value: string) => string;
}): { figure: PlotlyFigure | null; labelledCellsCount: number } => {
  const strippedBaseFigure = stripPlotTitle(baseFigure);

  if (!barcodeResponse) {
    return { figure: strippedBaseFigure, labelledCellsCount: 0 };
  }

  if (!slideMarkerQcSummary.length || !barcodeResponse.markerIds.length || !barcodeResponse.orderedSlideIds.length) {
    return {
      figure: strippedBaseFigure,
      labelledCellsCount: barcodeResponse.labelledCellsCount || 0,
    };
  }

  const qcLabelsById = Object.fromEntries(qcLabels.map((qcLabel) => [qcLabel.id, qcLabel]));
  const discoveredLabelNames = uniq(
    compact(
      slideMarkerQcSummary.flatMap((slideSummary) =>
        Object.values(slideSummary.channelMarkersQc || {}).map((qcValue) => {
          const qcLabelId = normalizeQcLabelId(qcValue);
          if (!qcLabelId) {
            return null;
          }

          return qcLabelsById[qcLabelId]?.name || qcLabelId;
        })
      )
    )
  );
  const orderedLabelNames = uniq([
    ...compact(qcLabels.map((qcLabel) => qcLabel.name || qcLabel.id)),
    ...discoveredLabelNames,
  ]);
  const labelOrderIndexByName = Object.fromEntries(orderedLabelNames.map((labelName, index) => [labelName, index]));
  const labelColorByName = Object.fromEntries(
    orderedLabelNames.map((labelName, index) => [labelName, QC_LABEL_PALETTE[index % QC_LABEL_PALETTE.length]])
  );
  const slideSummaryById = Object.fromEntries(
    slideMarkerQcSummary.map((slideSummary) => [slideSummary.slideId, slideSummary])
  );
  const markerLabels = barcodeResponse.markerIds.map(
    (markerId, index) => barcodeResponse.markerLabels?.[index] || stainTypeIdToDisplayName(markerId) || markerId
  );
  const tracesByKey = new Map<string, Record<string, unknown>>();
  let labelledCellsCount = 0;

  const getTraceSortOrder = (traceKey: string) => {
    if (traceKey === BARCODE_MISSING_TRACE_STYLE.key) return 0;
    if (traceKey === BARCODE_PRESENT_WITHOUT_QC_TRACE_STYLE.key) return 1;
    if (traceKey.startsWith('manual-label:')) {
      const labelName = traceKey.replace('manual-label:', '');
      return 10 + (labelOrderIndexByName[labelName] ?? orderedLabelNames.length);
    }
    if (traceKey.startsWith('manual-impact:')) {
      const impactId = traceKey.replace('manual-impact:', '');
      const impactIndex = BARCODE_IMPACT_ORDER.indexOf(impactId);
      return 20 + (impactIndex >= 0 ? impactIndex : BARCODE_IMPACT_ORDER.length);
    }
    if (traceKey.startsWith('global-impact:')) {
      const impactId = traceKey.replace('global-impact:', '');
      const impactIndex = BARCODE_IMPACT_ORDER.indexOf(impactId);
      return 30 + (impactIndex >= 0 ? impactIndex : BARCODE_IMPACT_ORDER.length);
    }

    return 99;
  };

  const ensureTrace = (style: BarcodeTraceStyle) => {
    const existingTrace = tracesByKey.get(style.key);
    if (existingTrace) {
      return existingTrace;
    }

    const trace = {
      type: 'scatter',
      mode: 'markers',
      name: style.name,
      x: [] as number[],
      y: [] as string[],
      customdata: [] as unknown[][],
      legendgroup: style.sourceLabel,
      marker: {
        symbol: style.symbol,
        size: 12,
        color: style.color,
        line: {
          color: style.color,
          width: style.lineWidth ?? 1,
        },
      },
      hovertemplate:
        style.key === BARCODE_MISSING_TRACE_STYLE.key
          ? '<b>%{customdata[2]}</b><br>Slide: %{customdata[0]}<br>Slide ID: %{customdata[1]}<br>Status: %{customdata[6]}<extra></extra>'
          : '<b>%{customdata[2]}</b><br>Slide: %{customdata[0]}<br>Slide ID: %{customdata[1]}<br>Channel: %{customdata[3]}<br>Manual QC label: %{customdata[4]}<br>Resolved impact: %{customdata[5]}<br>Status: %{customdata[6]}<br>Source: %{customdata[7]}<extra></extra>',
    };

    tracesByKey.set(style.key, trace);
    return trace;
  };

  barcodeResponse.markerIds.forEach((markerId, markerIndex) => {
    const markerLabel = markerLabels[markerIndex];

    barcodeResponse.orderedSlideIds.forEach((slideId, slideIndex) => {
      const slideLabel = barcodeResponse.orderedSlideLabels?.[slideIndex] || slideId;
      const slideSummary = slideSummaryById[slideId];
      const channelEntry = Object.entries(slideSummary?.channelMarkerTypes || {}).find(
        ([, summaryMarkerId]) => summaryMarkerId === markerId
      );

      let traceStyle = BARCODE_MISSING_TRACE_STYLE;
      let channelId = '';
      let manualLabelName = '—';
      let impactName = '—';

      if (channelEntry) {
        channelId = String(channelEntry[0]);
        const qcLabelId = normalizeQcLabelId(
          (slideSummary?.channelMarkersQc as Record<string, unknown> | null | undefined)?.[channelId]
        );
        const manualLabel = qcLabelId ? qcLabelsById[qcLabelId] : undefined;
        const manualImpactId = manualLabel?.impactId || null;
        const manualImpactName = manualImpactId ? IMPACT_LABELS[manualImpactId] || manualImpactId : '—';
        const globalImpactId = cohortMarkerQcSettings[markerId] || null;

        if (qcLabelId) {
          labelledCellsCount += 1;
          manualLabelName = manualLabel?.name || qcLabelId;
          impactName = manualImpactName;

          if (colorBy === 'label') {
            traceStyle = {
              key: `manual-label:${manualLabelName}`,
              name: manualLabelName,
              color: labelColorByName[manualLabelName] || QC_LABEL_PALETTE[0],
              symbol: 'square',
              stateLabel: 'Manual QC label',
              sourceLabel: 'Manual QC',
            };
          } else {
            const traceImpactName = manualImpactName !== '—' ? manualImpactName : 'QC labelled';
            traceStyle = {
              key: `manual-impact:${manualImpactId || 'qc_labeled'}`,
              name: traceImpactName,
              color: manualImpactId
                ? IMPACT_BAR_COLORS[manualImpactId] || BARCODE_MANUAL_QC_FALLBACK_COLOR
                : BARCODE_MANUAL_QC_FALLBACK_COLOR,
              symbol: 'square',
              stateLabel: 'Manual QC impact',
              sourceLabel: 'Manual QC',
            };
          }
        } else if (showGlobalImpact && globalImpactId) {
          labelledCellsCount += 1;
          impactName = IMPACT_LABELS[globalImpactId] || globalImpactId;
          traceStyle = {
            key: `global-impact:${globalImpactId}`,
            name: `Global: ${impactName}`,
            color: GLOBAL_IMPACT_BAR_COLORS[globalImpactId] || BARCODE_GLOBAL_QC_FALLBACK_COLOR,
            symbol: 'diamond',
            stateLabel: 'Global QC default',
            sourceLabel: 'Global QC',
            lineWidth: 1.2,
          };
        } else {
          traceStyle = BARCODE_PRESENT_WITHOUT_QC_TRACE_STYLE;
        }
      }

      const trace = ensureTrace(traceStyle);
      (trace.x as number[]).push(slideIndex);
      (trace.y as string[]).push(markerLabel);
      (trace.customdata as unknown[][]).push([
        slideLabel,
        slideId,
        markerLabel,
        channelId,
        manualLabelName,
        impactName,
        traceStyle.stateLabel,
        traceStyle.sourceLabel,
      ]);
    });
  });

  const traces = Array.from(tracesByKey.entries())
    .sort(([traceKeyA], [traceKeyB]) => getTraceSortOrder(traceKeyA) - getTraceSortOrder(traceKeyB))
    .map(([, trace]) => trace);

  const baseLayout = (strippedBaseFigure?.layout || {}) as Record<string, unknown>;
  const baseMeta =
    baseLayout.meta && typeof baseLayout.meta === 'object' ? { ...(baseLayout.meta as Record<string, unknown>) } : {};

  baseMeta.chartSummary = {
    slideCount: barcodeResponse.orderedSlideIds.length,
    markerCount: barcodeResponse.markerIds.length,
    labelledCellsCount,
  };

  return {
    figure: {
      ...strippedBaseFigure,
      data: traces,
      layout: {
        ...baseLayout,
        meta: baseMeta,
        margin: {
          ...((baseLayout.margin as Record<string, unknown> | undefined) || {}),
          l: 96,
          r: 24,
          t: 24,
          b: 140,
        },
        xaxis: {
          ...((baseLayout.xaxis as Record<string, unknown> | undefined) || {}),
          title: { text: 'Slides ordered by QC-labelled marker density' },
          showticklabels: false,
          showgrid: false,
          zeroline: false,
          range: [-0.5, Math.max(barcodeResponse.orderedSlideIds.length - 0.5, 0.5)],
        },
        yaxis: {
          ...((baseLayout.yaxis as Record<string, unknown> | undefined) || {}),
          title: { text: 'Marker' },
          type: 'category',
          autorange: 'reversed',
          categoryorder: 'array',
          categoryarray: markerLabels,
          showgrid: false,
          zeroline: false,
        },
        legend: {
          ...((baseLayout.legend as Record<string, unknown> | undefined) || {}),
          orientation: 'h',
          x: 0,
          xanchor: 'left',
          y: -0.2,
          yanchor: 'top',
          tracegroupgap: 12,
        },
        hovermode: 'closest',
        showlegend: true,
      },
    },
    labelledCellsCount,
  };
};

const QCMarkerBarcodeChart: React.FC<QCMarkerBarcodeChartProps> = ({
  studyId,
  qcMarkersSettings,
  markerIds = [],
  scope = 'study',
  renderMode = 'all',
  showGlobalImpact: controlledShowGlobalImpact,
  onShowGlobalImpactChange,
  barcodeColorBy: controlledBarcodeColorBy,
  onBarcodeColorByChange,
}) => {
  const { casesParams } = useCasesParams();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const { data: qcLabels = [] } = useQCLabels();
  const cohortMarkerQcSettings = useMemo(
    () => Object.fromEntries(Object.entries(qcMarkersSettings || {}).filter(([, impactId]) => Boolean(impactId))),
    [qcMarkersSettings]
  );

  const filteredCasesParams = useMemo(() => {
    if (scope === 'study') {
      return {
        studyId,
        labId: casesParams.labId,
        slidesMode: false,
        resultsMode: casesParams.resultsMode,
        filters: {
          studyId,
        },
      };
    }

    return {
      ...casesParams,
      studyId,
      filters: {
        ...(casesParams.filters || {}),
        studyId,
      },
    };
  }, [casesParams, scope, studyId]);

  const { data: slideMarkerQcSummary, isLoading: isLoadingSummary } = useQuery<SlideMarkerQcSummary[]>({
    queryKey: ['study-marker-qc-summary', scope, filteredCasesParams],
    queryFn: ({ signal }) => getSlideMarkerQcSummary(filteredCasesParams, signal),
    enabled: Boolean(studyId),
  });

  const knownLabelledCellsCount = useMemo(() => {
    if (!slideMarkerQcSummary) {
      return undefined;
    }

    return sum(slideMarkerQcSummary.map((slideSummary) => Object.keys(slideSummary.channelMarkersQc || {}).length));
  }, [slideMarkerQcSummary]);

  const slideLevelMarkedMarkerIds = useMemo(
    () => getSlideLevelQcMarkerIds(slideMarkerQcSummary),
    [slideMarkerQcSummary]
  );

  const cohortLevelMarkedMarkerIds = useMemo(
    () => uniq(compact(markerIds.length ? markerIds : keys(qcMarkersSettings || {}))),
    [markerIds, qcMarkersSettings]
  );

  const knownMarkerIds = useMemo(
    () => uniq(compact([...cohortLevelMarkedMarkerIds, ...slideLevelMarkedMarkerIds])),
    [cohortLevelMarkedMarkerIds, slideLevelMarkedMarkerIds]
  );
  const effectiveMarkerIds = knownMarkerIds;
  const knownSlideCount = slideMarkerQcSummary?.length;

  const [barcodeChartEl, setBarcodeChartEl] = useState<HTMLElement | null>(null);
  const [markerQcSummaryChartEl, setMarkerQcSummaryChartEl] = useState<HTMLElement | null>(null);
  const [showSummarySlideTable, setShowSummarySlideTable] = useState(false);
  const [internalShowGlobalImpact, setInternalShowGlobalImpact] = useState(true);
  const [internalBarcodeColorBy, setInternalBarcodeColorBy] = useState<BarcodeColorMode>('impact');
  const showSummarySections = renderMode !== 'barcode';
  const showBarcodeSection = renderMode !== 'summary';
  const showGlobalImpact = controlledShowGlobalImpact ?? internalShowGlobalImpact;
  const barcodeColorBy = controlledBarcodeColorBy ?? internalBarcodeColorBy;
  const updateShowGlobalImpact = React.useCallback(
    (checked: boolean) => {
      if (onShowGlobalImpactChange) {
        onShowGlobalImpactChange(checked);
        return;
      }

      setInternalShowGlobalImpact(checked);
    },
    [onShowGlobalImpactChange]
  );
  const updateBarcodeColorBy = React.useCallback(
    (value: BarcodeColorMode) => {
      if (onBarcodeColorByChange) {
        onBarcodeColorByChange(value);
        return;
      }

      setInternalBarcodeColorBy(value);
    },
    [onBarcodeColorByChange]
  );

  const { data: barcodeResponse, isLoading } = useQuery<SlideMarkerQcBarcodeResponse>({
    queryKey: ['study-marker-qc-barcode', scope, filteredCasesParams, effectiveMarkerIds, cohortMarkerQcSettings],
    queryFn: ({ signal }) =>
      getSlideMarkerQcBarcode(
        {
          casesParams: filteredCasesParams,
          markerIds: effectiveMarkerIds.length ? effectiveMarkerIds : undefined,
          cohortMarkerQcSettings: Object.keys(cohortMarkerQcSettings).length ? cohortMarkerQcSettings : undefined,
        },
        signal
      ),
    enabled: Boolean(studyId) && !isLoadingSummary,
  });

  const placeholderSlidesCount = useMemo(() => {
    if ((barcodeResponse?.slideCount || 0) > 0) {
      return Math.min(Math.max(barcodeResponse?.slideCount || 0, 8), 32);
    }

    if ((knownSlideCount || 0) > 0) {
      return Math.min(Math.max(knownSlideCount || 0, 8), 32);
    }

    return 16;
  }, [barcodeResponse?.slideCount, knownSlideCount]);

  const { figure: displayFigure, labelledCellsCount: displayedLabelledCellsCount } = useMemo(
    () =>
      buildSlideMarkerQcBarcodeFigure({
        baseFigure: barcodeResponse?.plotlyFigure,
        barcodeResponse,
        slideMarkerQcSummary: slideMarkerQcSummary || [],
        qcLabels,
        cohortMarkerQcSettings,
        colorBy: barcodeColorBy,
        showGlobalImpact,
        stainTypeIdToDisplayName,
      }),
    [
      barcodeColorBy,
      barcodeResponse,
      cohortMarkerQcSettings,
      qcLabels,
      showGlobalImpact,
      slideMarkerQcSummary,
      stainTypeIdToDisplayName,
    ]
  );
  const figureHeight = useMemo(
    () => Math.max(380, (barcodeResponse?.markerCount || knownMarkerIds.length || 1) * 34 + 220),
    [barcodeResponse?.markerCount, knownMarkerIds.length]
  );
  const markerQcSummaryRows = useMemo(
    () =>
      buildMarkerQcSummaryRows({
        markerIds: effectiveMarkerIds,
        qcLabels,
        slideMarkerQcSummary: slideMarkerQcSummary || [],
        cohortMarkerQcSettings,
        stainTypeIdToDisplayName,
      }),
    [cohortMarkerQcSettings, effectiveMarkerIds, qcLabels, slideMarkerQcSummary, stainTypeIdToDisplayName]
  );
  const markerQcSummaryFigure = useMemo(() => buildMarkerQcSummaryFigure(markerQcSummaryRows), [markerQcSummaryRows]);
  const markerQcSummaryFigureHeight = useMemo(
    () =>
      getMarkerQcSummaryFigureHeight({
        markerCount: markerQcSummaryRows.filter(
          (markerSummaryRow) => getDisplayedMarkerQcSummaryTotal(markerSummaryRow) > 0
        ).length,
        maxDisplayedStackValue: Math.max(
          ...markerQcSummaryRows.map((markerSummaryRow) => getDisplayedMarkerQcSummaryTotal(markerSummaryRow)),
          0
        ),
      }),
    [markerQcSummaryRows]
  );
  const slideMarkerQcDetailRows = useMemo(
    () =>
      buildSlideMarkerQcDetailRows({
        slideMarkerQcSummary: slideMarkerQcSummary || [],
        qcLabels,
        cohortMarkerQcSettings,
        stainTypeIdToDisplayName,
      }),
    [cohortMarkerQcSettings, qcLabels, slideMarkerQcSummary, stainTypeIdToDisplayName]
  );
  const { summaries: groupedSummaries, uniqueLabels: summaryUniqueLabels } = useMemo(
    () =>
      buildMarkerQcGroupedSummary({
        markerIds: effectiveMarkerIds,
        qcLabels,
        slideMarkerQcSummary: slideMarkerQcSummary || [],
        cohortMarkerQcSettings,
        stainTypeIdToDisplayName,
      }),
    [cohortMarkerQcSettings, effectiveMarkerIds, qcLabels, slideMarkerQcSummary, stainTypeIdToDisplayName]
  );
  const groupedSummaryFigure = useMemo(
    () =>
      buildGroupedMarkerQcSummaryFigure({
        summaries: groupedSummaries,
        uniqueLabels: summaryUniqueLabels,
        showGlobalImpact,
      }),
    [groupedSummaries, summaryUniqueLabels, showGlobalImpact]
  );
  const groupedSummaryFigureHeight = useMemo(() => {
    const activeCount = groupedSummaries.filter(
      (s) => Object.values(s.labelCounts).some(Boolean) || Object.values(s.impactCounts).some(Boolean)
    ).length;
    const maxStack = Math.max(
      ...groupedSummaries.map((s) => Math.max(sum(Object.values(s.labelCounts)), sum(Object.values(s.impactCounts)))),
      1
    );
    return Math.max(420, activeCount * 76 + 220, Math.min(1040, maxStack * 34 + 300));
  }, [groupedSummaries]);
  const hasSummaryContent = Boolean(groupedSummaryFigure || slideMarkerQcDetailRows.length);
  const cardTitle =
    renderMode === 'summary'
      ? 'Slide-Level Marker QC Summary'
      : renderMode === 'barcode'
      ? 'Slide-Level Marker QC Drilldown'
      : 'Slide-Level Marker QC';
  const cardDescription =
    renderMode === 'summary'
      ? 'Review the marker QC summary charts and per-slide tables for the selected slide set.'
      : renderMode === 'barcode'
      ? 'Drill into individual slide-marker QC cells after reviewing the summaries and cohort-level defaults above.'
      : 'Each row is a marker and each bar is a slide. Slides share the same left-to-right order across all markers and are sorted by how many marker QC labels they contain, then by file name. Hover a bar to see the slide ID, channel, and marker QC label details.';

  if (isLoading || isLoadingSummary) {
    if (knownMarkerIds.length) {
      return (
        <LoadingMarkerQcBarcodeChart
          markerIds={knownMarkerIds}
          placeholderSlidesCount={placeholderSlidesCount}
          slideCount={knownSlideCount}
          labelledCellsCount={knownLabelledCellsCount}
          qcMarkersSettings={qcMarkersSettings}
          stainTypeIdToDisplayName={stainTypeIdToDisplayName}
        />
      );
    }

    return (
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Box display="flex" alignItems="center" gap={1.5}>
          <CircularProgress size={20} />
          <Typography>
            {renderMode === 'summary'
              ? 'Loading slide-level marker QC summary…'
              : renderMode === 'barcode'
              ? 'Loading slide-level marker QC drilldown…'
              : 'Loading slide-level marker QC barcode…'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!barcodeResponse?.slideCount) {
    return (
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="h4" gutterBottom>
          {cardTitle}
        </Typography>
        <Typography color="text.secondary">
          {scope === 'study'
            ? 'No slides are currently included in this study slide set.'
            : 'No slides are currently included in the filtered dashboard slide set.'}
        </Typography>
      </Paper>
    );
  }

  if (showBarcodeSection && (!barcodeResponse.markerCount || !displayFigure)) {
    return (
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="h4" gutterBottom>
          {cardTitle}
        </Typography>
        <Typography color="text.secondary">
          {scope === 'study'
            ? 'No multiplex marker assignments were found for this study slide set.'
            : 'No multiplex marker assignments were found for the filtered dashboard slide set.'}
        </Typography>
      </Paper>
    );
  }

  if (showSummarySections && !showBarcodeSection && !hasSummaryContent) {
    return (
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Typography variant="h4" gutterBottom>
          {cardTitle}
        </Typography>
        <Typography color="text.secondary">
          No slide-level marker QC summaries are currently available for this slide set.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
      <Typography variant="h4" gutterBottom>
        {cardTitle}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {cardDescription}
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
        <Typography variant="body2">Slides: {barcodeResponse.slideCount}</Typography>
        <Typography variant="body2">Markers: {barcodeResponse.markerCount}</Typography>
        <Typography variant="body2">Labelled cells: {displayedLabelledCellsCount}</Typography>
      </Box>

      {showSummarySections ? <Divider sx={{ mb: 2 }} /> : null}

      {showSummarySections && groupedSummaryFigure ? (
        <>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 2 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Marker QC Summary
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Per-marker grouped bars: QC labels (left) and resolved impact (right) from{' '}
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  color="secondary"
                  sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                  onClick={() => setShowSummarySlideTable((prev) => !prev)}
                >
                  {groupedSummaries[0]?.totalSlides ?? 0} unique slides
                </Link>
                .
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showGlobalImpact}
                    onChange={(_, checked) => updateShowGlobalImpact(checked)}
                    inputProps={{ 'aria-label': 'Show global QC in summary' }}
                  />
                }
                label={<Typography variant="caption">Global QC</Typography>}
                sx={{ mr: 0 }}
              />
              <PlotlyChartActions
                chartEl={markerQcSummaryChartEl}
                figure={groupedSummaryFigure}
                title="Marker QC summary"
              />
            </Box>
          </Box>

          <Collapse in={showSummarySlideTable}>
            <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 2 }}>
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Slide</TableCell>
                      <TableCell>File name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(slideMarkerQcSummary || []).map((slideSummary) => (
                      <TableRow key={slideSummary.slideId} hover>
                        <TableCell>
                          <Link
                            href={`#/slides/${slideSummary.slideId}?caseStudyId=${studyId}${
                              filteredCasesParams.labId ? `&labId=${filteredCasesParams.labId}` : ''
                            }`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="secondary"
                          >
                            {slideSummary.slideId}
                          </Link>
                        </TableCell>
                        <TableCell>{slideSummary.originalFileName || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Collapse>

          <ThemedPlotlyFigure
            figure={groupedSummaryFigure}
            height={groupedSummaryFigureHeight}
            onPlotlyInit={setMarkerQcSummaryChartEl}
          />
        </>
      ) : null}

      {showSummarySections && slideMarkerQcDetailRows.length > 0 ? (
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Per-Slide Marker QC Detail
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
            Each row shows one slide × marker combination with its manual QC label, the resolved impact
            {showGlobalImpact ? ', and the cohort-level global QC setting' : ''}.
          </Typography>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table
                stickyHeader
                size="small"
                sx={{ '& .MuiTableCell-stickyHeader': { backgroundColor: 'background.paper' } }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Slide</TableCell>
                    <TableCell>File name</TableCell>
                    <TableCell>Marker</TableCell>
                    <TableCell>QC (manual)</TableCell>
                    <TableCell>QC Impact</TableCell>
                    {showGlobalImpact ? <TableCell>Global QC</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slideMarkerQcDetailRows.map((row) => (
                    <TableRow key={`${row.slideId}-${row.markerId}`} hover>
                      <TableCell>
                        <Link
                          href={`#/slides/${row.slideId}?caseStudyId=${studyId}${
                            filteredCasesParams.labId ? `&labId=${filteredCasesParams.labId}` : ''
                          }`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="secondary"
                        >
                          {row.slideId}
                        </Link>
                      </TableCell>
                      <TableCell>{row.originalFileName || '—'}</TableCell>
                      <TableCell>{row.markerLabel}</TableCell>
                      <TableCell>{row.qcLabelName || '—'}</TableCell>
                      <TableCell>
                        <ImpactChip impactId={row.qcImpactId} />
                      </TableCell>
                      {showGlobalImpact ? (
                        <TableCell>
                          <ImpactChip impactId={row.globalQcImpactId} />
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : null}

      {showSummarySections && showBarcodeSection ? <Divider sx={{ my: 3 }} /> : null}

      {showBarcodeSection ? (
        <>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {renderMode !== 'barcode' ? (
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Slide-marker barcode
                </Typography>
              ) : null}
              <Typography color="text.secondary" variant="body2">
                Switch between resolved impact and manual QC labels, and toggle global QC defaults on or off for the
                barcode view.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showGlobalImpact}
                    onChange={(_, checked) => updateShowGlobalImpact(checked)}
                    inputProps={{ 'aria-label': 'Show global QC in barcode' }}
                  />
                }
                label={<Typography variant="caption">Global QC</Typography>}
                sx={{ mr: 0 }}
              />
              <ToggleButtonGroup
                value={barcodeColorBy}
                exclusive
                size="small"
                onChange={(_, value) => {
                  if (value) updateBarcodeColorBy(value);
                }}
                sx={{ height: 28 }}
              >
                <ToggleButton value="impact" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem' }}>
                  Impact
                </ToggleButton>
                <ToggleButton value="label" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem' }}>
                  Manual label
                </ToggleButton>
              </ToggleButtonGroup>
              <PlotlyChartActions
                chartEl={barcodeChartEl}
                figure={displayFigure}
                title="Slide-level marker QC barcode"
              />
            </Box>
          </Box>

          <ThemedPlotlyFigure figure={displayFigure} height={figureHeight} onPlotlyInit={setBarcodeChartEl} />
        </>
      ) : null}
    </Paper>
  );
};

export default QCMarkerBarcodeChart;
